import React, { useCallback, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, AllCommunityModule, ModuleRegistry, themeBalham } from 'ag-grid-community';
import { format, isValid } from 'date-fns';
import * as XLSX from 'xlsx';
import { TJournalisation } from '../../../utils/types/TJournalisation';

ModuleRegistry.registerModules([AllCommunityModule]);

interface JournalisationGridProps {
    journalisations: TJournalisation[];
}

const JournalisationGrid: React.FC<JournalisationGridProps> = ({ journalisations }) => {
    const gridRef = useRef<AgGridReact<TJournalisation>>(null);

    const [columnDefs] = useState<ColDef<TJournalisation>[]>([
        {
            field: 'date',
            headerName: 'Date & Heure',
            sortable: true,
            filter: 'agDateColumnFilter',
            width: 160,
            valueFormatter: (params) => {
                if (!params.value) return '';
                const date = new Date(params.value);
                return isValid(date) ? format(date, 'dd/MM/yyyy HH:mm:ss') : '';
            },
        },
        { field: 'tableOperation', headerName: 'Table', sortable: true, filter: true, width: 150 },
        {
            field: 'typeOperation',
            headerName: 'Opération',
            sortable: true,
            filter: true,
            width: 120,
            cellStyle: (params) => {
                if (params.value === 'DELETE') return { color: 'red', fontWeight: 'bold' };
                if (params.value === 'INSERT') return { color: 'green', fontWeight: 'bold' };
                if (params.value === 'UPDATE') return { color: 'orange', fontWeight: 'bold' };
                return null;
            },
        },
        { field: 'idUtilisateur', headerName: 'ID User', sortable: true, filter: true, width: 100 },
        {
            field: 'ancienEnregistrement',
            headerName: 'Ancienne Valeur',
            sortable: false,
            filter: true,
            flex: 1,
            tooltipField: 'ancienEnregistrement',
        },
        {
            field: 'nouvelEnregistrement',
            headerName: 'Nouvelle Valeur',
            sortable: false,
            filter: true,
            flex: 1,
            tooltipField: 'nouvelEnregistrement',
        },
    ]);

    const myTheme = themeBalham.withParams({ columnBorder: { style: 'dashed', width: '1px' } });

    const exportAsCsv = useCallback(() => {
        if (!gridRef.current) return;

        const timestamp = format(new Date(), 'yyyy-MM-dd_HH-mm');

        gridRef.current.api.exportDataAsCsv({
            fileName: `journalisation_export_${timestamp}.csv`,
            allColumns: false,
        });
    }, []);

    const exportAsExcel = useCallback(() => {
        if (!gridRef.current) return;

        const visibleCols = gridRef.current.api.getColumns()?.filter((col) => !col.getColDef().hide);
        if (!visibleCols || visibleCols.length === 0) {
            alert('Aucune colonne visible à exporter');
            return;
        }

        const rowData: TJournalisation[] = [];
        gridRef.current.api.forEachNodeAfterFilterAndSort((node) => {
            if (node.data) rowData.push(node.data);
        });

        if (rowData.length === 0) {
            alert('Aucune donnée à exporter');
            return;
        }

        const exportData = rowData.map((row) => {
            const obj: Record<string, any> = {};
            visibleCols.forEach((col) => {
                const def = col.getColDef();
                const field = def.field as keyof TJournalisation;
                const header = def.headerName ?? field ?? '';

                if (!field) return;

                let value = row[field];

                if (field === 'date' && value) {
                    const date = new Date(value as Date);
                    value = isValid(date) ? format(date, 'dd/MM/yyyy HH:mm:ss') : '';
                }

                obj[header] = value;
            });
            return obj;
        });

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        // Ajustement automatique basique des largeurs de colonnes pour Excel
        const wscols = visibleCols.map(() => ({ wch: 20 }));
        worksheet['!cols'] = wscols;

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Logs');

        const timestamp = format(new Date(), 'yyyy-MM-dd_HH-mm');
        XLSX.writeFile(workbook, `journalisation_export_${timestamp}.xlsx`);
    }, []);

    return (
        <>
            <div style={{ height: '100vh', width: '100%', display: 'flex', flexDirection: 'column' }}>
                <div style={{ flex: 1, width: '100%' }}>
                    <AgGridReact<TJournalisation>
                        ref={gridRef}
                        theme={myTheme}
                        getRowId={(params) => params.data.idJournalisation.toString()}
                        rowData={journalisations}
                        columnDefs={columnDefs}
                        pagination={true}
                        paginationPageSize={50}
                        domLayout="normal"
                        defaultColDef={{
                            resizable: true,
                            minWidth: 100,
                            tooltipComponent: 'customTooltip',
                        }}
                        enableCellTextSelection={true}
                        ensureDomOrder={true}
                        animateRows={true}
                        suppressExcelExport={true}
                        overlayNoRowsTemplate="<span style='padding: 10px;'>Aucune donnée ...</span>"
                    />
                </div>
            </div>

            {/* Bouton d'exportation */}
            <div className="dropdown mt-2">
                <button className="btn btn-secondary dropdown-toggle" type="button" id="exportDropdown" data-bs-toggle="dropdown" aria-expanded="false">
                    <i className="bi bi-download me-2"></i>Export
                </button>
                <ul className="dropdown-menu" aria-labelledby="exportDropdown">
                    <li>
                        <button className="dropdown-item" onClick={exportAsCsv}>
                            Export as CSV
                        </button>
                    </li>
                    <li>
                        <button className="dropdown-item" onClick={exportAsExcel}>
                            Export as Excel
                        </button>
                    </li>
                </ul>
            </div>
        </>
    );
};

export default JournalisationGrid;
